/**
 * Assignment Periods Query Hooks (Epic 3 — Toewijzing met tijdspercentages
 * en tijdvakken, Story 3.2)
 *
 * Eigen hook-bestand (i.p.v. uitbreiding van `useEmployeeTasks.ts`) —
 * kerntaken-toewijzing is een eigen, groeiend domein (Story 3.3 hergebruikt
 * `useTaskPeriods` voor de tijdlijn), consistent met het precedent uit
 * Epic 2 (`useCoreTasks.ts` als eigen bestand i.p.v. uitbreiding van
 * `useSettings.ts`).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { assignmentPeriodsApi } from '../../lib/assignmentPeriodsApi';
import type { AssignmentPeriodDTO, PeriodInput } from '@functieloonhuis/shared';

export const assignmentPeriodKeys = {
  all: ['taskPeriods'] as const,
  lists: () => [...assignmentPeriodKeys.all, 'list'] as const,
  list: (employeeId: number) => [...assignmentPeriodKeys.lists(), employeeId] as const,
};

/**
 * Alle tijdvakken (open + gesloten) van een medewerker, nieuwste eerst.
 * Hergebruikt door Story 3.3 (tijdlijn) — dezelfde query-key, geen aparte
 * "alle tijdvakken"-hook ernaast.
 */
export function useTaskPeriods(employeeId: number | undefined) {
  return useQuery({
    queryKey: assignmentPeriodKeys.list(employeeId ?? -1),
    queryFn: () => assignmentPeriodsApi.list(employeeId!),
    enabled: employeeId !== undefined,
  });
}

/** Het actuele (open) tijdvak van een medewerker — `null` als er geen is. */
export function useCurrentPackage(employeeId: number | undefined): {
  currentPackage: AssignmentPeriodDTO | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { data, isLoading, error } = useTaskPeriods(employeeId);
  const currentPackage = data?.find((period) => period.endDate === null) ?? null;
  return { currentPackage, isLoading, error: error as Error | null };
}

/**
 * Pakket opslaan: opent een nieuw tijdvak (geen `periodId`) of wijzigt een
 * bestaand tijdvak (`periodId` gezet). Invalideert de tijdvakken-lijst na
 * succes zodat zowel de "Actueel pakket"-kaart als de tijdlijn (3.3)
 * meteen de nieuwe staat tonen.
 */
export function useSaveTaskPeriod(employeeId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, input }: { periodId?: number; input: PeriodInput }) => {
      if (employeeId === undefined) {
        return Promise.reject(new Error('employeeId is vereist'));
      }
      return periodId !== undefined
        ? assignmentPeriodsApi.update(employeeId, periodId, input)
        : assignmentPeriodsApi.open(employeeId, input);
    },
    onSuccess: () => {
      if (employeeId !== undefined) {
        queryClient.invalidateQueries({ queryKey: assignmentPeriodKeys.list(employeeId) });
      }
    },
  });
}
